import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';


const TrustBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({
    years: 0,
    customers: 0,
    locations: 0,
    rating: 0
  });
  const statsRef = useRef(null);

  const partners = [
    {
      name: "Mercedes-Benz",
      logo: "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
    },
    {
      name: "Mazda",
      logo: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
    },
    {
      name: "Ford",
      logo: "https://images.unsplash.com/photo-1612825173281-9a193378527e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
    }
  ];

  const location = {
    city: "Dinslaken",
    phone: "02064 777722",
    address: "Otto-Lilienthal-Straße 43, 46539 Dinslaken"
  };

  // Intersection Observer to detect when stats come into view with mobile optimization
  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - trigger animation immediately
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { 
        threshold: 0.2, // Lower threshold for mobile
        rootMargin: '0px 0px -20px 0px' // Reduced margin for mobile
      }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [isVisible]);

  // Counting animation with mobile optimization
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 seconds
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const frameRate = isMobile ? 30 : 60; // Lower frame rate for mobile
    const steps = Math.floor((duration / 1000) * frameRate);
    const stepDuration = duration / steps;

    const targetValues = {
      years: 15,
      customers: 500,
      locations: 2,
      rating: 4.9
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      setCounts({
        years: Math.floor(targetValues.years * easeOutQuart),
        customers: Math.floor(targetValues.customers * easeOutQuart),
        locations: Math.floor(targetValues.locations * easeOutQuart),
        rating: Math.round(targetValues.rating * easeOutQuart * 10) / 10
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts(targetValues);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section className="bg-white border-b py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Partner Logos */}
        <div className="text-center mb-8">
          <p className="text-sm text-muted-foreground mb-4">Vertrauen von führenden Automarken</p>
          <div className="flex items-center justify-center space-x-8 md:space-x-12">
            {partners?.map((partner, index) => (
              <div key={index} className="flex items-center space-x-2 opacity-60 hover:opacity-100 transition-opacity">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Car" size={16} color="white" />
                </div>
                <span className="text-sm font-medium text-foreground">{partner?.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Location Info */}
        <div className="flex justify-center">
          <div className="flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={16} className="text-accent" />
              <span className="font-semibold text-foreground">{location?.city}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={16} className="text-primary" />
              <a 
                href={`tel:+49${location?.phone?.replace(/\s/g, '')}`}
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                {location?.phone}
              </a>
            </div>
            <div className="text-xs text-muted-foreground">
              2 Standorte in Dinslaken
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-primary will-change-transform">{counts.years}+</div>
            <div className="text-sm text-muted-foreground">Jahre Erfahrung</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-primary will-change-transform">{counts.customers.toLocaleString()}+</div>
            <div className="text-sm text-muted-foreground">Zufriedene Kunden</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-primary will-change-transform">{counts.locations}</div>
            <div className="text-sm text-muted-foreground">Standort</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-primary will-change-transform">{counts.rating}★</div>
            <div className="text-sm text-muted-foreground">Kundenbewertung</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustBar;